/* eslint-disable camelcase */
import { makeStyles, TextField, Typography } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import { Autocomplete } from '@material-ui/lab'
import throttle from 'lodash/throttle'
import LocationOnIcon from 'mdi-react/MapMarkerIcon'
import { useState, useRef, useMemo, useEffect } from 'react'
import {
  getPlacesApiUrl,
  getLocationInfo
} from '../../../services/googlePlaces'
import { AutocompleteOption } from '../../../types/autocomplete'
import postalCodesToDistricts from '../../../utils/apartment/postalCodesToDistricts.json'
import { findCityOption } from '../../../utils/validation/listOfCitiesForPostalArea'
// Supported countries
const componentRestrictions = {
  // Finland, Åland Islands
  country: ['fi', 'ax']
}

// Autocomple types
// see https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service
// and https://developers.google.com/places/supported_types#table3
const types = ['address']

const loadScript = (src: string, position: HTMLElement | null, id: string) => {
  if (!position) {
    return
  }

  // Google Maps recommends loading with async/defer and a callback
  if (typeof window !== 'undefined' && !(window as any).googleMapsInit) {
    (window as any).googleMapsInit = () => { }
  }

  const script = document.createElement('script')
  script.setAttribute('async', '')
  script.setAttribute('defer', '')
  script.setAttribute('id', id)
  script.src = src
  position.appendChild(script)
}

let autocompleteService: google.maps.places.AutocompleteService

const useStyles = makeStyles((theme) => ({
  displayNone: {
    display: 'none'
  },
  endAdornment: {
    right: '12px'
  },
  input: {
    height: '58px',
    fontSize: '13px',
    boxSizing: 'border-box',
    border: '1px solid rgb(232, 232, 232)',
    borderRadius: '6px',
    background: 'rgb(231, 236, 239)',
    padding: '20px !important'
  },
  inputRoot: {
    paddingRight: '0 !important',
    '&::after': {
      display: 'none'
    },
    '&::before': {
      display: 'none'
    }
  }
}))

const ReduxGoogleAutocomplete = ({
  input,
  label,
  meta,
  isFromClientForm = false,
  isFromApartmentForm = false,
  streetNumberFormValue,
  ...custom
}: any) => {
  const classes = useStyles()
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<AutocompleteOption[]>(
    custom.existingLocations
  )
  const [selectedPlace, setSelectedPlace] = useState<
    AutocompleteOption | undefined
  >()
  const changeFormValue = custom.changeFormValue || (() => { })
  const loaded = useRef(false)
  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        getPlacesApiUrl(),
        document.querySelector('head'),
        'google-maps'
      )
    }

    loaded.current = true
  }

  const fetchPlacesInfo = useMemo(
    () =>
      throttle((input: any, callback: any) => {
        autocompleteService.getPlacePredictions(input, callback)
      }, 200),
    []
  )

  useEffect(() => {
    let active = true

    if (!autocompleteService && window.google?.maps?.places) {
      autocompleteService = new window.google.maps.places.AutocompleteService()
    }
    if (!autocompleteService) {
      return undefined
    }

    if (inputValue === '') {
      return undefined
    }

    fetchPlacesInfo(
      { input: inputValue, types, componentRestrictions },
      (results?: any[]) => {
        if (active) {
          setOptions(
            results
              ? results.map(({ place_id, description }) => {
                const parts = description.split(',')
                  .map(part => part.trim())
                  .filter(part => !/^(Finland|Suomi)$/i.test(part)) // filter out 'Suomi' or 'Finland'
                return {
                  value: place_id,
                  label: parts.join(', ')
                }
              })
              : []
          )
        }
      }
    )

    return () => {
      active = false
    }
  }, [inputValue, fetchPlacesInfo])

  const [hasFocusedToStreetNumber, setHasFocusedToStreetNumber] = useState(false);
  const lastLookupRef = useRef<{ place?: AutocompleteOption; streetNumber?: string }>({})

  useEffect(() => {
    const applyLocationToForm = (location: any) => {
      if (!location) return

      const postalCode = location.postalCode || ''
      const postalArea = location.postalArea || ''
      const googlePlaceId = location.googlePlaceId || ''
      const streetNumberMatch = location.streetAddress
        ? location.streetAddress.match(/\b\d+\b/)
        : null
      const streetNumberFromPlace = streetNumberMatch ? streetNumberMatch[0] : ''

      if (streetNumberFromPlace && isFromApartmentForm) {
        changeFormValue('streetNumber', streetNumberFromPlace)
      }
      if (postalCode) {
        changeFormValue('postalCode', postalCode)
      }
      if (isFromApartmentForm) {
        // Listing location is the municipality; unknown names are cleared (null, not undefined) so the user picks one
        changeFormValue('postalArea', findCityOption(location.municipality) || null)
        if (postalCode && postalCodesToDistricts[postalCode]) {
          const district = postalCodesToDistricts[postalCode]
          changeFormValue('district', district)
        } else {
          changeFormValue('district', '');
        }
      } else if (postalArea) {
        changeFormValue('postalArea', postalArea)
        if (!isFromClientForm) {
          changeFormValue('googlePlaceId', googlePlaceId)
          changeFormValue('coordinates', location.coordinates)
        }
      }
    }

    if (selectedPlace && selectedPlace.value && selectedPlace.label) {
      // `custom` changes on every render; look up only when the place or street number changes,
      // otherwise a re-render would overwrite fields the user has edited since
      const lastLookup = lastLookupRef.current
      if (lastLookup.place === selectedPlace && lastLookup.streetNumber === streetNumberFormValue) {
        return
      }
      lastLookupRef.current = { place: selectedPlace, streetNumber: streetNumberFormValue }

      const placeId = selectedPlace.value
      const streetAddress = selectedPlace.label

      const fetchLocation = async () => {
        const baseLocation = await getLocationInfo(placeId, streetAddress)
        applyLocationToForm(baseLocation)

        // If postalCode missing but user has already provided street number, retry with override
        if (!baseLocation.postalCode && streetNumberFormValue) {
          const retryLocation = await getLocationInfo(placeId, streetAddress, {
            streetNumberOverride: streetNumberFormValue
          })
          applyLocationToForm({ ...baseLocation, ...retryLocation })
        }
      }
      fetchLocation().then(() => {
        if (!hasFocusedToStreetNumber) {
          setTimeout(() => {
            const input = document.getElementById('field-streetNumber')
            input?.focus()
            setHasFocusedToStreetNumber(true)
          }, 0)
        }
      })
    }
  }, [selectedPlace, custom, streetNumberFormValue, isFromApartmentForm])

  const { touched, error, active } = meta
  return (
    <Autocomplete
      id='google-map-location'
      value={input.value || null}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.label.split(',')[0]
      }
      filterOptions={(x) => x}
      onChange={(event: any, value: any) => {
        if (value?.label) {
          value.label = value.label.split(',')[0]
          setInputValue(value.label)

          if (value?.value) {
            setSelectedPlace(value)
          }
        }
        input.onChange(value)
        input.onBlur()
      }}
      classes={{
        input: classes.input,
        inputRoot: classes.inputRoot,
        endAdornment: classes.endAdornment
      }}
      onOpen={() => input.onBlur()}
      onClose={() => input.onBlur()}
      options={options}
      autoComplete
      includeInputInList
      freeSolo
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          onDrop={() => input.onDrop()}
          onFocus={() => input.onFocus()}
          onDragStart={() => input.onDragStart()}
          onBlur={() => input.onBlur()}
          fullWidth
          placeholder={custom.placeholder}
          onKeyDown={(e) => {
            if (/\d/.test(e.key) && isFromApartmentForm) {
              e.preventDefault()
            }
          }}
          onChange={(event: any) => {
            let newValue = event.target.value;

            if (isFromApartmentForm) {
              newValue = newValue.replace(/[0-9]/g, '');
            }

            setInputValue(newValue);
            input.onChange(newValue);
          }}
          required={custom.required || false}
          error={!active && !!touched && !!error}
          helperText={!active && !!touched && error}
          autoComplete='off'
        />
      )}
      renderOption={(option) => {
        return (
          <Grid container alignItems='center'>
            <Grid item>
              <LocationOnIcon />
            </Grid>
            <Grid item xs>
              <Typography variant='body2'>
                {option.label} {custom.selectLabel || ''}
              </Typography>
            </Grid>
          </Grid>
        )
      }}
    />
  )
}

export default ReduxGoogleAutocomplete
